import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Grid,
    Paper,
    Tabs,
    Tab,
    Avatar,
    CircularProgress,
    Alert,
} from '@mui/material';
import {
    Close as CloseIcon,
    CloudUpload as CloudUploadIcon,
    CameraAlt as CameraIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import UserAvatar from './UserAvatar';

interface ProfilePictureModalProps {
    open: boolean;
    onClose: () => void;
    user: {
        id?: number | string;
        username?: string;
        firstName?: string;
        lastName?: string;
        email?: string;
        profilePicture?: string;
    };
    onSave: (imageUrl: string | null) => Promise<void>;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
};

// Predefined avatar styles
const AVATAR_STYLES = [
    { name: 'Gradient', type: 'gradient' },
    { name: 'Solid', type: 'solid' },
    { name: 'Pattern', type: 'pattern' },
    { name: 'Geometric', type: 'geometric' },
];

const AVATAR_COLORS = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
    '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7B731',
    '#5F27CD', '#00D2D3', '#FF9FF3', '#54A0FF',
    '#48DBFB', '#0ABDE3', '#EE5353', '#10AC84'
];

const ProfilePictureModal: React.FC<ProfilePictureModalProps> = ({
    open,
    onClose,
    user,
    onSave,
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(user.profilePicture || null);
    const [selectedStyle, setSelectedStyle] = useState(0);
    const [selectedColor, setSelectedColor] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        setError(null);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be less than 5MB');
                return;
            }
            if (!file.type.startsWith('image/')) {
                setError('Please select an image file');
                return;
            }
            
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
            setError(null);
        }
    };

    const generateAvatarUrl = (): string => {
        // Generate a unique avatar based on selected style and color
        // Style is used for future pattern variations (AVATAR_STYLES[selectedStyle].type)
        const color = AVATAR_COLORS[selectedColor].replace('#', '');
        const name = `${user.firstName || ''}+${user.lastName || user.username || ''}`;
        
        // Using UI Avatars service as an example, but can be replaced with custom generation
        return `https://ui-avatars.com/api/?name=${name}&background=${color}&color=fff&size=200&font-size=0.4&bold=true`;
    };

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        
        try {
            let imageUrl: string | null = null;
            
            if (tabValue === 0 && selectedFile) {
                // Upload the selected file
                // In a real implementation, this would upload to a server
                // For now, we'll use the preview URL
                imageUrl = previewUrl;
            } else if (tabValue === 1) {
                // Use generated avatar
                imageUrl = generateAvatarUrl();
            }
            
            await onSave(imageUrl);
            onClose();
        } catch (err) {
            setError('Failed to update profile picture');
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async () => {
        setLoading(true);
        try {
            await onSave(null);
            onClose();
        } catch (err) {
            setError('Failed to remove profile picture');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Update Profile Picture</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <UserAvatar
                        user={{
                            ...user,
                            profilePicture: tabValue === 0 ? previewUrl || user.profilePicture : generateAvatarUrl()
                        }}
                        size={120}
                    />
                </Box>
                
                <Tabs value={tabValue} onChange={handleTabChange} centered>
                    <Tab label="Upload Photo" icon={<CloudUploadIcon />} />
                    <Tab label="Generate Avatar" icon={<RefreshIcon />} />
                </Tabs>
                
                <TabPanel value={tabValue} index={0}>
                    <Box sx={{ textAlign: 'center' }}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                        
                        <Button
                            variant="contained"
                            startIcon={<CameraIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            size="large"
                            fullWidth
                            sx={{ mb: 2 }}
                        >
                            Choose Photo
                        </Button>
                        
                        <Typography variant="caption" color="text.secondary">
                            Maximum file size: 5MB. Supported formats: JPG, PNG, GIF
                        </Typography>
                        
                        {selectedFile && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2">
                                    Selected: {selectedFile.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </TabPanel>
                
                <TabPanel value={tabValue} index={1}>
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Avatar Style
                        </Typography>
                        <Grid container spacing={1} sx={{ mb: 3 }}>
                            {AVATAR_STYLES.map((style, index) => (
                                <Grid item xs={3} key={style.name}>
                                    <Paper
                                        elevation={selectedStyle === index ? 4 : 1}
                                        sx={{
                                            p: 1,
                                            cursor: 'pointer',
                                            border: selectedStyle === index ? 2 : 0,
                                            borderColor: 'primary.main',
                                            textAlign: 'center',
                                        }}
                                        onClick={() => setSelectedStyle(index)}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                mx: 'auto',
                                                mb: 0.5,
                                                background: `linear-gradient(135deg, ${AVATAR_COLORS[selectedColor]} 0%, ${AVATAR_COLORS[(selectedColor + 1) % AVATAR_COLORS.length]} 100%)`
                                            }}
                                        >
                                            {user.firstName?.[0] || user.username?.[0] || 'U'}
                                        </Avatar>
                                        <Typography variant="caption">
                                            {style.name}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        
                        <Typography variant="subtitle2" gutterBottom>
                            Color Theme
                        </Typography>
                        <Grid container spacing={1}>
                            {AVATAR_COLORS.map((color, index) => (
                                <Grid item key={color}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            backgroundColor: color,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            border: selectedColor === index ? 3 : 0,
                                            borderColor: 'common.white',
                                            boxShadow: selectedColor === index ? 3 : 1,
                                        }}
                                        onClick={() => setSelectedColor(index)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </TabPanel>
            </DialogContent>
            
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button
                    startIcon={<DeleteIcon />}
                    onClick={handleRemove}
                    color="error"
                    disabled={loading || !user.profilePicture}
                >
                    Remove
                </Button>
                <Box>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading || (tabValue === 0 && !selectedFile && !user.profilePicture)}
                        startIcon={loading && <CircularProgress size={20} />}
                    >
                        Save
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ProfilePictureModal;